import { ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { colors } from '~/styles/colors';
import { CheckBox } from '../../ds';
import { FULL_DAY, SchedulePermission } from '../../schedules';
import { Toggle } from '../controls';
import { CheckIcon } from '../icons';
import { ModalShell } from './ModalShell';
import {
  BackButton, InlineCheck, ModalBody, ModalButtons, ModalError, ModalLabel, ModalSubtitle, ModalTitle,
  PermissionLabel, PermissionRow, SubmitButton, TextInput, TimeField, TimeInput, TimeRow,
} from './styles';

export interface ExceptionModalValues {
  /** AAAA-MM-DD, o formato do campo de data e da chave da exceção. */
  date: string;
  start: string;
  end: string;
  fullDay: boolean;
  permission: SchedulePermission;
}

export const EMPTY_EXCEPTION_VALUES: ExceptionModalValues = {
  date: '', start: '', end: '', fullDay: false, permission: 'allow',
};

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface ExceptionModalProps {
  initialValues: ExceptionModalValues;
  isEdit: boolean;
  onClose: () => void;
  onSubmit: (values: ExceptionModalValues) => void;
}

export function ExceptionModal({
  initialValues, isEdit, onClose, onSubmit,
}: Readonly<ExceptionModalProps>): ReactElement {
  const { t } = useTranslation();
  const {
    control, register, handleSubmit, watch, formState: { errors },
  } = useForm<ExceptionModalValues>({ defaultValues: initialValues });
  const fullDay = watch('fullDay');

  function submit(values: ExceptionModalValues): void {
    onSubmit(values.fullDay ? { ...values, start: FULL_DAY.start, end: FULL_DAY.end } : values);
  }

  return (
    <ModalShell width={520} onClose={onClose}>
      <ModalBody onSubmit={handleSubmit(submit)} noValidate data-testid="eco-simplificado-modal-excecao">
        <ModalTitle>{t(isEdit ? 'ecoSimplificadoEditarExcecao' : 'ecoSimplificadoAdicionarExcecaoTitulo')}</ModalTitle>
        <ModalSubtitle>{t('ecoSimplificadoExcecaoSubtitulo')}</ModalSubtitle>

        <ModalLabel htmlFor="eco-simplificado-dia">{t('dia')}</ModalLabel>
        <TextInput
          id="eco-simplificado-dia"
          type="date"
          min={todayIso()}
          {...register('date', {
            validate: (value) => {
              if (!value) return t('obrigatorio');
              return value >= todayIso() || value === initialValues.date || t('dataDeveSerFutura');
            },
          })}
        />
        {errors.date ? <ModalError>{errors.date.message}</ModalError> : null}

        <TimeRow>
          <TimeField>
            <ModalLabel htmlFor="eco-simplificado-de">{t('ecoSimplificadoDe')}</ModalLabel>
            <TimeInput
              id="eco-simplificado-de"
              type="time"
              disabled={fullDay}
              {...register('start', { validate: (value, values) => values.fullDay || !!value || t('obrigatorio') })}
            />
            {errors.start ? <ModalError>{errors.start.message}</ModalError> : null}
          </TimeField>
          <TimeField>
            <ModalLabel htmlFor="eco-simplificado-ate">{t('ecoSimplificadoAte')}</ModalLabel>
            <TimeInput
              id="eco-simplificado-ate"
              type="time"
              disabled={fullDay}
              {...register('end', {
                validate: (value, values) => {
                  if (values.fullDay) return true;
                  if (!value) return t('obrigatorio');
                  return !values.start || value > values.start || t('horarioFinalMaiorInicial');
                },
              })}
            />
            {errors.end ? <ModalError>{errors.end.message}</ModalError> : null}
          </TimeField>
          <InlineCheck>
            <input type="checkbox" {...register('fullDay')} />
            <CheckBox $checked={fullDay}><CheckIcon size={12} strokeWidth={3} color={colors.text['white-0']} /></CheckBox>
            {t('ecoSimplificado24Horas')}
          </InlineCheck>
        </TimeRow>

        <ModalLabel as="div" style={{ marginTop: 22, marginBottom: 10 }}>{t('funcionamento')}</ModalLabel>
        <Controller
          control={control}
          name="permission"
          render={({ field }) => (
            <PermissionRow>
              <PermissionLabel $active={field.value === 'allow'}>{t('ecoSimplificadoPermitirRefrigeracao')}</PermissionLabel>
              <Toggle
                checked={field.value === 'allow'}
                onChange={(allow) => field.onChange(allow ? 'allow' : 'forbid')}
                label={t('funcionamento')}
              />
              <PermissionLabel $active={field.value === 'forbid'}>{t('ecoSimplificadoBloquear')}</PermissionLabel>
            </PermissionRow>
          )}
        />

        <ModalButtons>
          <BackButton type="button" onClick={onClose}>{t('botaoVoltar')}</BackButton>
          <SubmitButton type="submit">{t(isEdit ? 'ecoSimplificadoSalvar' : 'botaoAdicionar')}</SubmitButton>
        </ModalButtons>
      </ModalBody>
    </ModalShell>
  );
}
